import { useMemo } from "react"
import {
  CheckCircle,
  Clock,
  DotsHorizontal,
  Mail01,
  Send01,
  Trash01,
  Users01,
  UserPlus01,
} from "@untitledui/icons"
import type { ApiTeamMemberInvite, TeamMemberSummary } from "@/lib/api/types"
import { DesignMenu } from "@/components/ui/design-menu"
import { useTeamPermissions } from "@/lib/team/use-team-permissions"
import { cx } from "@/lib/utils/cx"

// Unified directory of accepted members + pending invites for a single
// team. Single list, status pills, per-row ⋯ menu.
//
//   - Member ⋯: Change role · Remove from team
//   - Invite ⋯: Resend invite · Revoke
//   - Hidden when `canManageMembers` is false (read-only view)
//
// Pills use onboarding's chip vocabulary: rounded (not full), 10px uppercase
// tracking-wide, soft tinted bg, no ring. Identical to the chip shape in
// team-row-card.tsx so Settings and onboarding feel like one product.

function initialsOf(s: string): string {
  return s
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

function memberDisplayName(m: { firstName: string; lastName: string; email: string }) {
  const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim()
  return name || m.email
}

function inviteDisplayName(i: ApiTeamMemberInvite) {
  const name = [i.user.firstName, i.user.lastName].filter(Boolean).join(" ").trim()
  return name || i.user.email
}

type DirectoryRow =
  | { kind: "member"; key: string; member: TeamMemberSummary }
  | { kind: "invite"; key: string; invite: ApiTeamMemberInvite }

interface TeamMembersTableProps {
  // Per-team teamId so useTeamPermissions reads the user's role on THIS team
  // (not whichever team is active in OrganizationContext).
  teamId: number | null | undefined
  members: TeamMemberSummary[]
  pendingInvites: ApiTeamMemberInvite[]
  isLoading?: boolean
  // Disabled when the BE 404s on pending invites (older mocks didn't have
  // the endpoint). The table just renders members in that case.
  invitesUnavailable?: boolean
  onInvite?: () => void
  onResendInvite?: (invite: ApiTeamMemberInvite) => void
  onRevokeInvite?: (invite: ApiTeamMemberInvite) => void
  onChangeRole?: (member: TeamMemberSummary) => void
  onRemoveMember?: (member: TeamMemberSummary) => void
}

export function TeamMembersTable({
  teamId,
  members,
  pendingInvites,
  isLoading,
  invitesUnavailable,
  onInvite,
  onResendInvite,
  onRevokeInvite,
  onChangeRole,
  onRemoveMember,
}: TeamMembersTableProps) {
  const perms = useTeamPermissions(teamId)

  const rows: DirectoryRow[] = useMemo(() => {
    const m: DirectoryRow[] = members.map((member) => ({
      kind: "member",
      key: `m-${member.email.toLowerCase()}`,
      member,
    }))
    const i: DirectoryRow[] = (invitesUnavailable ? [] : pendingInvites).map((invite) => ({
      kind: "invite",
      key: `i-${invite.uuid}`,
      invite,
    }))
    return [...m, ...i]
  }, [members, pendingInvites, invitesUnavailable])

  const totalCount = rows.length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[12.5px] text-zinc-500">
          {totalCount === 0
            ? "No one yet."
            : totalCount === 1
              ? "1 person"
              : `${totalCount} people`}
        </p>
        {perms.canInvite && onInvite && (
          <button
            type="button"
            onClick={onInvite}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13px] font-semibold shadow-md shadow-zinc-900/15 transition-all duration-150"
          >
            <Send01 className="size-3.5" aria-hidden />
            Invite member
          </button>
        )}
      </div>

      {isLoading ? (
        <RowSkeletons count={3} />
      ) : totalCount === 0 ? (
        <EmptyState canInvite={perms.canInvite} onInvite={onInvite} />
      ) : (
        <div className="space-y-2">
          {rows.map((row) =>
            row.kind === "member" ? (
              <MemberRow
                key={row.key}
                member={row.member}
                canManage={perms.canManageMembers}
                onChangeRole={onChangeRole}
                onRemove={onRemoveMember}
              />
            ) : (
              <InviteRow
                key={row.key}
                invite={row.invite}
                canManage={perms.canManageMembers}
                onResend={onResendInvite}
                onRevoke={onRevokeInvite}
              />
            ),
          )}
        </div>
      )}
    </div>
  )
}

function RowSkeletons({ count }: { count: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-zinc-200/70 bg-white p-3 flex items-center gap-3 animate-pulse"
        >
          <div className="size-10 rounded-xl bg-zinc-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 rounded bg-zinc-100" />
            <div className="h-2.5 w-48 rounded bg-zinc-100" />
          </div>
          <div className="h-5 w-16 rounded-full bg-zinc-100" />
        </div>
      ))}
    </div>
  )
}

function MemberRow({
  member,
  canManage,
  onChangeRole,
  onRemove,
}: {
  member: TeamMemberSummary
  canManage: boolean
  onChangeRole?: (m: TeamMemberSummary) => void
  onRemove?: (m: TeamMemberSummary) => void
}) {
  const name = memberDisplayName(member)
  const hasMenu = canManage && (onChangeRole || onRemove)
  return (
    <div className="rounded-2xl border border-zinc-200/70 bg-white p-3 flex items-center gap-3 hover:border-zinc-300 transition-colors duration-150">
      <Avatar text={name} email={member.email} />
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-zinc-900 truncate leading-tight">{name}</p>
        <p className="text-[12px] text-zinc-500 truncate mt-0.5 leading-tight">{member.email}</p>
      </div>
      <RolePill role={member.role} />
      <StatusPill status="active" />
      {hasMenu ? (
        <DesignMenu.Root>
          <DesignMenu.Trigger asChild>
            <button
              type="button"
              className="size-8 rounded-lg grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-150 shrink-0"
              aria-label={`Actions for ${name}`}
            >
              <DotsHorizontal className="size-4" aria-hidden />
            </button>
          </DesignMenu.Trigger>
          <DesignMenu.Content align="end">
            {onChangeRole && (
              <DesignMenu.Item
                onSelect={() => onChangeRole(member)}
                icon={<UserPlus01 className="text-zinc-500" aria-hidden />}
              >
                Change role
              </DesignMenu.Item>
            )}
            {onChangeRole && onRemove && <DesignMenu.Separator />}
            {onRemove && (
              <DesignMenu.Item
                destructive
                onSelect={() => onRemove(member)}
                icon={<Trash01 aria-hidden />}
              >
                Remove from team
              </DesignMenu.Item>
            )}
          </DesignMenu.Content>
        </DesignMenu.Root>
      ) : (
        <span className="size-8 shrink-0" aria-hidden />
      )}
    </div>
  )
}

function InviteRow({
  invite,
  canManage,
  onResend,
  onRevoke,
}: {
  invite: ApiTeamMemberInvite
  canManage: boolean
  onResend?: (i: ApiTeamMemberInvite) => void
  onRevoke?: (i: ApiTeamMemberInvite) => void
}) {
  const name = inviteDisplayName(invite)
  return (
    <div className="rounded-2xl border border-amber-200/70 bg-amber-50/30 p-3 flex items-center gap-3 hover:border-amber-300 transition-colors duration-150">
      <Avatar text={name} email={invite.user.email} pending />
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-zinc-900 truncate leading-tight">{name}</p>
        <p className="text-[12px] text-zinc-500 truncate mt-0.5 leading-tight">{invite.user.email}</p>
      </div>
      <RolePill role={invite.role} />
      <StatusPill status="pending" />
      {canManage ? (
        <DesignMenu.Root>
          <DesignMenu.Trigger asChild>
            <button
              type="button"
              className="size-8 rounded-lg grid place-items-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors duration-150 shrink-0"
              aria-label={`Actions for invite to ${invite.user.email}`}
            >
              <DotsHorizontal className="size-4" aria-hidden />
            </button>
          </DesignMenu.Trigger>
          <DesignMenu.Content align="end">
            {onResend && (
              <DesignMenu.Item
                onSelect={() => onResend(invite)}
                icon={<Send01 className="text-zinc-500" aria-hidden />}
              >
                Resend invite email
              </DesignMenu.Item>
            )}
            {onResend && <DesignMenu.Separator />}
            <DesignMenu.Item
              destructive
              onSelect={() => onRevoke?.(invite)}
              icon={<Trash01 aria-hidden />}
            >
              Revoke invite
            </DesignMenu.Item>
          </DesignMenu.Content>
        </DesignMenu.Root>
      ) : (
        <span className="size-8 shrink-0" aria-hidden />
      )}
    </div>
  )
}

function Avatar({
  text,
  email,
  pending,
}: {
  text: string
  email?: string
  pending?: boolean
}) {
  const fallback = email ? email[0]?.toUpperCase() : "·"
  return (
    <div
      className={cx(
        "size-10 rounded-xl grid place-items-center shrink-0 text-[12.5px] font-bold",
        pending
          ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200/70"
          : "bg-zinc-100 text-zinc-700",
      )}
    >
      {pending ? <Mail01 className="size-4" aria-hidden /> : initialsOf(text) || fallback}
    </div>
  )
}

function RolePill({ role }: { role: string }) {
  return (
    <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-600 whitespace-nowrap">
      {role}
    </span>
  )
}

function StatusPill({ status }: { status: "active" | "pending" | "paused" }) {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 whitespace-nowrap">
        <CheckCircle className="size-2.5" aria-hidden />
        Active
      </span>
    )
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 whitespace-nowrap">
        <Clock className="size-2.5" aria-hidden />
        Pending
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-500 whitespace-nowrap">
      Paused
    </span>
  )
}

function EmptyState({
  canInvite,
  onInvite,
}: {
  canInvite: boolean
  onInvite?: () => void
}) {
  return (
    <div className="px-5 py-10 flex flex-col items-center text-center">
      <div className="size-12 rounded-2xl bg-zinc-100 grid place-items-center mb-3">
        <Users01 className="size-5 text-zinc-400" aria-hidden />
      </div>
      <p className="text-[14px] font-semibold text-zinc-900">It's just you here</p>
      <p className="text-[12.5px] text-zinc-500 mt-1 max-w-xs">
        {canInvite
          ? "Send invites so your team can see estimates and updates."
          : "Your team admin can invite more people."}
      </p>
      {canInvite && onInvite && (
        <button
          type="button"
          onClick={onInvite}
          className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13px] font-semibold shadow-sm transition-colors duration-150"
        >
          <Send01 className="size-3.5" aria-hidden />
          Invite first member
        </button>
      )}
    </div>
  )
}
